import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CarePhase,
  CareEnvironment,
  carePhaseDefs,
  careEnvironmentsApplicableToPhase,
  pickClientCareEnvironmentCosts,
} from '@shared';
import { putCareEnvironmentSelectionsForClientByClientIdRequest, selectClient } from '@/model';

export function useCareSettingsSlide(carePhase: CarePhase) {
  const client = useSelector(selectClient)!;

  const {
    clientFirstName,
    intakeSurvey: { clientBirthYear, clientCurrentCarePhase, clientPhaseStartAges },
    phaseCosts,
    clientPhasePredictedStartYears,
    clientPhaseInflationFactors,
  } = client;

  const dispatch = useDispatch();
  const [selectedCareSetting, setSelectedCareSetting] = useState<number | null>(
    client.careEnvironmentSelections?.[carePhase] ?? null,
  );

  const carePhaseIndex = carePhaseDefs[carePhase].index;
  const selectedCarePhaseCosts = phaseCosts[carePhaseIndex];
  const predictedPhaseStartYear = clientPhasePredictedStartYears[carePhase];
  const phaseAge = predictedPhaseStartYear
    ? predictedPhaseStartYear - clientBirthYear
    : clientPhaseStartAges[carePhase]!;
  const carePhaseDurationMonths = selectedCarePhaseCosts.phaseCareMonthsNeeded;
  const applicableCareSettings = careEnvironmentsApplicableToPhase[carePhase];
  const carePhaseStartAge = clientPhaseStartAges[carePhase];
  const isDurationCustom = selectedCarePhaseCosts.isDurationCustom;
  const isCareSettingHome = selectedCareSetting === CareEnvironment.home;
  const phaseInflationFactor = clientPhaseInflationFactors[carePhase]!;

  const handleButtonClick = (careSettingDbId: number) => {
    if (careSettingDbId === selectedCareSetting) {
      setSelectedCareSetting(null);
      dispatch(
        putCareEnvironmentSelectionsForClientByClientIdRequest({
          clientId: client.clientId,
          careEnvironmentSelections: {
            ...(client.careEnvironmentSelections ?? {}),
            [carePhase]: null,
          },
        }),
      );
      return;
    }

    setSelectedCareSetting(careSettingDbId);

    dispatch(
      putCareEnvironmentSelectionsForClientByClientIdRequest({
        clientId: client.clientId,
        careEnvironmentSelections: {
          ...(client.careEnvironmentSelections ?? {}),
          [carePhase]: careSettingDbId,
        },
      }),
    );
  };

  const careSettingCosts = applicableCareSettings.map(setting => ({
    dbId: setting.dbId,
    cost:
      setting.dbId === CareEnvironment.home
        ? calcInflatedMaximumFamilyShareCostFor(
            selectedCarePhaseCosts,
            client,
            phaseInflationFactor,
          )
        : calcInflatedProfessionalShareCostFor(
            selectedCarePhaseCosts,
            client,
            phaseInflationFactor,
            setting.dbId as CareEnvironment,
          ),
    isHome: setting.dbId === CareEnvironment.home,
  }));

  return {
    client,
    clientFirstName,
    clientCurrentCarePhase,
    carePhaseIndex,
    selectedCarePhaseCosts,
    phaseAge,
    carePhaseDurationMonths,
    applicableCareSettings,
    carePhaseStartAge,
    isDurationCustom,
    selectedCareSetting,
    isCareSettingHome,
    handleButtonClick,
    careSettingCosts,
    nextButtonLabel: determineNextSlideLabel(isCareSettingHome, carePhase, clientFirstName),
  };
}

function determineNextSlideLabel(
  isCareSettingHome: boolean,
  carePhase: CarePhase,
  clientFirstName: string,
) {
  if (isCareSettingHome) {
    return `Next`;
  }

  return carePhaseDefs[carePhase].index === 2
    ? `Review ${clientFirstName}'s Costs`
    : `Review ${carePhaseDefs[carePhase + 1].label}`;
}

function calcInflatedMaximumFamilyShareCostFor(
  phaseCosts: SinglePhaseCosts,
  client: Client,
  phaseInflationFactor: number,
) {
  const careEnvironmentCostData = pickClientCareEnvironmentCosts(client, CareEnvironment.home);

  return (
    careEnvironmentCostData.rateAmount * phaseCosts.phaseCareHoursNeeded * phaseInflationFactor
  );
}

function calcInflatedProfessionalShareCostFor(
  phaseCosts: SinglePhaseCosts,
  client: Client,
  phaseInflationFactor: number,
  careEnvironment: CareEnvironment,
) {
  const careEnvironmentCostData = pickClientCareEnvironmentCosts(client, careEnvironment);

  return (
    careEnvironmentCostData.rateAmount * phaseCosts.phaseCareMonthsNeeded * phaseInflationFactor
  );
}
